import { defineStore } from 'pinia'
import { useCmsFetch } from '@/composables/craft-fetch'
import { useCacheStore } from '~/store/cache'
import type { RouteLocationNormalizedGeneric as Route } from 'vue-router'
import { ignoreShelterSiteHandles } from '~/store/shelter'

const navigationFallback = Object.freeze({ bottom: [], footer: [], main: [], service: [], secondaryMenuTitle: undefined, cta: undefined })

const annotateNavigationItems = (items, openItemId, fullPath) => {
  return (
    items?.map((item) => {
      const children = annotateNavigationItems(item.children, openItemId, fullPath)
      return {
        ...item,
        isOpen: children.length && (children.some((child) => child.isOpen) || item.id === openItemId),
        isActive: children.some((child) => child.isActive) || (item.url && item.url?.match(/^(https?:\/\/[^/]+)?(?<fullPath>.*)/i)?.groups.fullPath === fullPath),
        children,
      }
    }) || []
  )
}

// Note that this storage is PARTIALLY set up as sessionStorage
export const useNavigationStore = defineStore('navigation', () => {
  const cacheStore = useCacheStore()
  const cmsFetch = useCmsFetch()

  const state = {
    // The previous URL
    referrer: ref<string | undefined>(undefined),
    // The current URL/path, used to determine what the active navigation element is
    fullPath: ref<string | undefined>(undefined),
    // The id of the current navigation item that is toggled open
    openItemId: ref<string | undefined>(undefined),
    // Navigation elements
    navigation: ref({ ...navigationFallback }),
  }

  state.bottom = computed(() => annotateNavigationItems(state.navigation.value.bottom, state.openItemId.value, state.fullPath.value))
  state.footer = computed(() => annotateNavigationItems(state.navigation.value.footer, state.openItemId.value, state.fullPath.value))
  state.main = computed(() => annotateNavigationItems(state.navigation.value.main, state.openItemId.value, state.fullPath.value))
  state.service = computed(() => annotateNavigationItems(state.navigation.value.service, state.openItemId.value, state.fullPath.value))

  // Values used in navigation components
  state.secondaryMenuTitle = computed(() => {
    return state.navigation.value.secondaryMenuTitle
  })
  state.cta = computed(() => state.navigation.value.cta)

  function closeAll() {
    state.openItemId.value = undefined
  }
  function setReferrer(from: string, to: string) {
    if (to !== from) {
      state.referrer.value = from
    }
  }
  function setRoute(route: Route) {
    state.fullPath.value = route.fullPath
  }
  function toggleOpen(id) {
    state.openItemId.value = state.openItemId.value === id ? undefined : id
  }
  async function fetchNavigation(site: string) {
    const key = `navigation;${site}`
    const ttl = 3600

    const fetcher = async () => {
      const [bottom, footer, main, service, shelter, cta] = await Promise.all([
        cmsFetch(`/api/site/${site === 'inspectiedienst' ? 'inspectiedienst' : 'default'}/navigation/bottom.json`),
        cmsFetch(`/api/site/${site}/navigation/footer.json`),
        cmsFetch(`/api/site/${site}/navigation/main.json`),
        cmsFetch(`/api/site/${site}/navigation/service.json`),
        ['default', 'ikZoekBaas', 'mijnDierIsZoek', 'inspectiedienst'].includes(site)
          ? undefined
          : cmsFetch(`/api/site/${site}/shelter.json`, ignoreShelterSiteHandles.includes(site) ? { notFoundStrategy: 'ignore' } : {}),
        cmsFetch(`/api/global/cta/${site}.json`),
      ])
      return { bottom: bottom.data, footer: footer.data, main: main.data, service: service.data, secondaryMenuTitle: shelter?.title, cta }
    }

    const navigation = await cacheStore.fetch({ key, ttl, fetcher, fallback: { ...navigationFallback } })

    state.navigation.value = navigation

    return navigation
  }

  return { ...state, closeAll, setReferrer, setRoute, toggleOpen, fetch: fetchNavigation }
})
